/* Pointex core JS — reveal on scroll, gallery, accordion, read-more, progress bars, counters.
   No dependencies. Safe to include multiple times (guards itself). */
(function () {
  if (window.__pxCore) return;
  window.__pxCore = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- reveal / bars / counters ---------- */
  function animateCounter(el) {
    if (el.__pxCounted) return; el.__pxCounted = true;
    var target = parseFloat(el.getAttribute('data-px-count')) || 0;
    var suffix = el.getAttribute('data-px-suffix') || '';
    var dur = parseInt(el.getAttribute('data-px-duration') || '1400', 10);
    if (reduce) { el.textContent = target + suffix; return; }
    var start = null;
    function step(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = Math.round(target * eased) + suffix;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  /* Urgency bar: jumps quickly from the start value, then crawls very slowly up to the target. */
  function loadBar(el) {
    var from = parseFloat(el.getAttribute('data-px-bar-from')) || 0;
    var to = parseFloat(el.getAttribute('data-px-bar')) || 0;
    var label = el.querySelector('[data-px-bar-count]');
    function set(v, shown) {
      el.style.setProperty('--px-fill', v + '%');
      if (label) label.textContent = shown + '%';
    }
    if (reduce || to <= from) { set(to, to); return; }
    var dur = 10000, start = null;
    set(from, from);
    function step(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      var v = from + (to - from) * (1 - Math.pow(1 - p, 5));
      set(v, p < 1 ? Math.floor(v) : to);
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function onEnter(el) {
    el.classList.add('is-in');
    if (el.hasAttribute('data-px-bar-from')) {
      loadBar(el);
    } else if (el.hasAttribute('data-px-bar')) {
      el.style.setProperty('--px-fill', (el.getAttribute('data-px-bar') || 0) + '%');
    }
    el.querySelectorAll('[data-px-count]').forEach(animateCounter);
    if (el.hasAttribute('data-px-count')) animateCounter(el);
  }

  var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) { onEnter(e.target); io.unobserve(e.target); }
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }) : null;

  function watch(root) {
    (root || document).querySelectorAll('.px-reveal, [data-px-bar], [data-px-count]').forEach(function (el) {
      if (el.__pxWatched) return;
      el.__pxWatched = true;
      if (io) io.observe(el); else onEnter(el);
    });
  }

  /* ---------- gallery ---------- */
  function initGallery(g) {
    if (g.__px) return; g.__px = true;
    var track = g.querySelector('[data-px-track]');
    var slides = Array.prototype.slice.call(g.querySelectorAll('[data-px-slide]'));
    var thumbs = Array.prototype.slice.call(g.querySelectorAll('[data-px-thumb]'));
    var dots = Array.prototype.slice.call(g.querySelectorAll('[data-px-dot]'));
    var prev = g.querySelector('[data-px-prev]');
    var next = g.querySelector('[data-px-next]');
    var counter = g.querySelector('[data-px-counter]');
    var current = 0;
    if (!track || !slides.length) return;

    function setActive(i) {
      current = i;
      thumbs.forEach(function (t, k) { t.classList.toggle('is-active', k === i); t.setAttribute('aria-current', k === i ? 'true' : 'false'); });
      dots.forEach(function (d, k) { d.classList.toggle('is-active', k === i); });
      if (counter) counter.textContent = (i + 1) + ' / ' + slides.length;
      var t = thumbs[i];
      if (t && t.scrollIntoView) {
        var wrap = t.parentElement;
        var left = t.offsetLeft - wrap.clientWidth / 2 + t.clientWidth / 2;
        wrap.scrollTo({ left: left, behavior: reduce ? 'auto' : 'smooth' });
      }
      slides.forEach(function (s, k) {
        var v = s.querySelector('video');
        if (!v) return;
        if (k === i) { v.play && v.play().catch(function () {}); } else { v.pause && v.pause(); }
      });
    }
    function goTo(i, behavior) {
      i = (i + slides.length) % slides.length;
      track.scrollTo({ left: slides[i].offsetLeft, behavior: behavior || (reduce ? 'auto' : 'smooth') });
      setActive(i);
    }
    thumbs.forEach(function (t, k) { t.addEventListener('click', function () { goTo(k); }); });
    dots.forEach(function (d, k) { d.addEventListener('click', function () { goTo(k); }); });
    if (prev) prev.addEventListener('click', function () { goTo(current - 1); });
    if (next) next.addEventListener('click', function () { goTo(current + 1); });

    var raf;
    track.addEventListener('scroll', function () {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        var x = track.scrollLeft, best = 0, dist = Infinity;
        slides.forEach(function (s, k) { var d = Math.abs(s.offsetLeft - x); if (d < dist) { dist = d; best = k; } });
        if (best !== current) setActive(best);
      });
    }, { passive: true });

    g.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { goTo(current + 1); }
      if (e.key === 'ArrowLeft') { goTo(current - 1); }
    });

    // Let apps (bundles / variant pickers) jump the gallery to a variant image.
    document.addEventListener('px:gallery:goto', function (e) {
      var id = e.detail && e.detail.mediaId;
      var idx = slides.findIndex(function (s) { return s.getAttribute('data-media-id') == id; });
      if (idx > -1) goTo(idx);
    });
    setActive(0);
  }

  /* ---------- accordion ---------- */
  function initAccordion(root) {
    (root || document).querySelectorAll('[data-px-acc]').forEach(function (btn) {
      if (btn.__px) return; btn.__px = true;
      btn.addEventListener('click', function () {
        var item = btn.closest('[data-px-acc-item]');
        var group = btn.closest('[data-px-acc-group]');
        var open = item.classList.contains('is-open');
        if (group && group.getAttribute('data-px-acc-group') === 'single') {
          group.querySelectorAll('[data-px-acc-item].is-open').forEach(function (o) {
            o.classList.remove('is-open'); o.querySelector('[data-px-acc]').setAttribute('aria-expanded', 'false');
          });
        }
        item.classList.toggle('is-open', !open);
        btn.setAttribute('aria-expanded', String(!open));
      });
    });
  }

  /* ---------- read more ---------- */
  function initReadMore(root) {
    (root || document).querySelectorAll('[data-px-more]').forEach(function (btn) {
      if (btn.__px) return; btn.__px = true;
      btn.addEventListener('click', function () {
        var box = btn.closest('[data-px-more-box]');
        var open = box.classList.toggle('is-open');
        btn.textContent = open ? (btn.getAttribute('data-less') || 'Read less') : (btn.getAttribute('data-more') || 'Read more');
      });
    });
  }

  /* ---------- smooth scroll to anchors ---------- */
  function initScrollTo(root) {
    (root || document).querySelectorAll('[data-px-scroll]').forEach(function (a) {
      if (a.__px) return; a.__px = true;
      a.addEventListener('click', function (e) {
        var sel = a.getAttribute('data-px-scroll');
        var target = sel && document.querySelector(sel);
        if (!target) return;
        e.preventDefault();
        var offset = parseInt(a.getAttribute('data-px-offset') || '80', 10);
        var top = target.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: top, behavior: reduce ? 'auto' : 'smooth' });
      });
    });
  }


  /* ---------- price pill inside the add-to-cart button ---------- */
  function initPricePill(root) {
    (root || document).querySelectorAll('[data-px-pill]').forEach(function (pill) {
      if (pill.__px) return; pill.__px = true;
      var buy = pill.closest('.px-buy') || document;
      var now = pill.querySelector('[data-px-pill-now]');
      var was = pill.querySelector('[data-px-pill-was]');
      var showCompare = pill.getAttribute('data-compare') !== 'false';
      var variants = {};
      try { var sc = buy.querySelector('[data-px-variants]'); variants = sc ? JSON.parse(sc.textContent) : {}; } catch (e) {}

      /* Prices from the variant JSON follow the shop money format, which can be HTML
         (currency apps set it to <span class=money>...</span> in some markets). */
      function put(el, v) { if (/</.test(v)) el.innerHTML = v; else el.textContent = v; }
      function set(p, c) {
        if (!p) { pill.hidden = true; return; }
        pill.hidden = false; put(now, p);
        if (was) { if (showCompare && c && c !== p) { put(was, c); was.hidden = false; } else { was.hidden = true; } }
      }
      function fromKaching(k) {
        var bar = k.querySelector('.kaching-bundles__bar--selected') || k;
        var p = bar.querySelector('.kaching-bundles__bar-price');
        var c = bar.querySelector('.kaching-bundles__bar-full-price');
        var pt = p ? p.textContent.trim() : '';
        if (pt) set(pt, c ? c.textContent.trim() : '');
        return !!pt;
      }
      function fromVariant() {
        var input = buy.querySelector('input[name="id"]');
        var v = input && variants[input.value];
        if (v && v.p) set(v.p, v.c);
      }
      function bindKaching() {
        var k = document.querySelector('kaching-bundles-block');
        if (!k) return false;
        if (k.__pxPill) return true;
        k.__pxPill = true;
        var timer;
        var update = function () { clearTimeout(timer); timer = setTimeout(function () { if (!fromKaching(k)) fromVariant(); }, 60); };
        ['deal-bar-selected', 'variant-selected', 'variants-changed'].forEach(function (ev) { k.addEventListener(ev, update); });
        if ('MutationObserver' in window) {
          new MutationObserver(update).observe(k, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class'] });
        }
        update();
        return true;
      }
      fromVariant();
      var input = buy.querySelector('input[name="id"]');
      if (input && 'MutationObserver' in window) {
        new MutationObserver(function () { if (!document.querySelector('kaching-bundles-block')) fromVariant(); }).observe(input, { attributes: true, attributeFilter: ['value'] });
        input.addEventListener('change', function () { if (!document.querySelector('kaching-bundles-block')) fromVariant(); });
      }
      if (!bindKaching()) {
        var tries = 0;
        var t = setInterval(function () { if (bindKaching() || ++tries > 60) clearInterval(t); }, 250);
      }
    });
  }

  function initAll(root) {
    watch(root);
    (root || document).querySelectorAll('[data-px-gallery]').forEach(initGallery);
    initAccordion(root); initReadMore(root); initScrollTo(root); initPricePill(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();

  // Theme editor: re-init when a section is (re)loaded.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  window.pxInit = initAll;
})();
